import React, { useReducer } from 'react';
import CovidEpassTrains from '../route_info/components/CovidEpassTrains';
import { BreadCrumb, FooterSection, FaqSection } from '../components';
import InfoCard from '../components/InfoCard';
import { TrainClasses } from '../route_info/TrainClasses';
import { formatDuration, formatDuration2 } from '../../utils/dateTimeFormatUtils';
import DataLoaderError from '../../errors/DataLoaderError';
import { EnvironmentVariables, CovidSpecialTrainsPageData } from '../types';
import { getAppEnv } from '../../../config/env';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import { FOOTER_DATA_IRCTC, IRCTC_EXTERNAL_JS } from '../../utils/RailsSEOConstants';
import { getStaticPageLoadEvent } from '../../utils/omnitureUtils';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

type CovidSpecialTrainsPageDataProps = {
  data: CovidSpecialTrainsPageData;
};
function decorateListingData(listingData) {
  const { covidSpecialTrains } = listingData;

  covidSpecialTrains.forEach((train) => {
    // if the current format is in format say 5hr, make it 5hr 0mins
    if (String(train.duration).endsWith('hr')) {
      train.duration = train.duration.concat(' 0mins');
    }
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration.replace('hr ', ':').replace('mins', ''));
  });
  return listingData;
}

const CovidSpecialTrainsPage = ({ data }: CovidSpecialTrainsPageDataProps) => {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainExtraSectionClicked } = SEARCH_WIDGET_UTILS.COVID_SPECIAL_TRAINS_UTILS(searchWidgetDispatch);

  return (
    <div>
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={false}
          topFix={true}
          classNode={TrainClasses.FC}
          pageWiseContext={SearchWidgetContext}
        />
      </SearchWidgetContext.Provider>
      <div className="container">
        <HtmlHeader data={data} />
        <BreadCrumb breadcrumb={data.template.breadcrumb} />

        <div className="heading">
          <h1 className="appendTop15 appendBottom15 font22">{data.template.h1_title}</h1>
        </div>
        <section>
          <CovidEpassTrains trainsList={data.covidSpecialTrains} onClicked={onTrainExtraSectionClicked} />
        </section>
        <section>
          <h2 className="desktopShow appendBottom15">{`Information regarding Covid19 Special Trains`}</h2>
          <InfoCard infos={data.infos} />
        </section>
        <FaqSection faq={data.faq} pageUrl={data.template.sample_url} type="covidSpecialTrains" />
      </div>
      <FooterSection template={data.template} />
    </div>
  );
};

CovidSpecialTrainsPage.fetchData = async (_, match, urlObj) => {
  const url = `${DATA_LOADER_URL}/api/getCovidSpecialTrainsData?refreshMode=static`;

  let responseData;
  const response = await fetch(url);
  // eslint-disable-next-line no-console
  console.info('url', url);
  // eslint-disable-next-line no-console
  console.info('response.status', url, response.status);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    throw new Error('JSON parse exception: ' + textResponse);
  }
  // eslint-disable-next-line no-console
  console.log('responseData.status', responseData.status);
  if (responseData.status !== 'SUCCESS') {
    throw new DataLoaderError(`Unable to fetch for covid special trains list`, responseData.error);
  }
  responseData.faq = [
    {
      question: 'Do these trains run daily? Where can I find out the latest schedule for all these new trains?',
      answer: `1.&nbspSome trains are running daily, while others are running bi-weekly and tri-weekly. Trains connecting to cities like Kolkata, Guwahati, Jammu, Jaipur, Ranchi, etc. are running daily as of now.\r\n<br/>\r\n
      2.&nbspBoth MMT app and website <a href="https://www.makemytrip.com/railways/covid-special-trains.html">(click here)</a> have the latest schedule, information on the routes taken and timings of when the trains reach every station.\r\n<br/>\r\n
      3.&nbspYou can also check which dates are available, what the fare is and availability of 2 tier/ 3 tier/ second sitting/ sleeper coaches in each train on our portals.\r\n<br/>\r\n
      4.&nbspIf your desired travel date is not available, you can click on the ‘Nearby Dates’ button to see the next best available date.`
    },
    {
      question: `How do I book these tickets and what is reservation/ tatkal booking policy?`,
      answer: `1. Tickets can be booked on the MakeMyTrip website, mobile app or IRCTC website. Reservation counters at the stations remain shut. Booking of tickets through any agents, is currently not allowed.\r\n<br/>\r\n
      2. No unreserved (UTS) tickets are being issued. Before boarding, all passengers must have a confirmed, valid and fully reserved ticket with them.\r\n<br/>\r\n
      3. Tatkal bookings are also permitted for these trains. However, passengers are advised to book in advance.`
    },
    {
      question: `What are the rules for travelling, if sick? `,
      answer: `All passengers will be medically screened while entering the station. Anyone with symptoms will not be allowed to board, even if they have valid tickets.`
    },
    {
      question: `Is it safe to travel by the IRCTC Covid-19 Special trains?`,
      answer: `Many precautions are being taken to ensure safety. These include:\r\n<br/>\r\n
      1. Thorough sanitisation of trains at both the originating and destination stations.\r\n<br/>\r\n
      2. Presence of sufficient, well-trained staff to ensure the washrooms are clean and regularly sanitised.\r\n<br/>\r\n
      3. Only limited eatables and packaged drinking water (on payment basis) are being provided on trains that have pantry coaches. For the rest, there will be no catering to minimise contact.\r\n<br/>\r\n
      4. No linen, blankets and curtains shall be provided inside the train. Passengers are advised to carry their own linen.\r\n<br/>\r\n
      5. Space permitting, most railway stations are arranging for different entry and exit gates to minimise face-to-face interaction between travellers.\r\n<br/>\r\n
      6. Only passengers with valid, confirmed travel tickets (not platform tickets) can enter the station.\r\n<br/>\r\n
      7. All passengers must wear a face cover at all times and undergo medical screening before starting their journey.\r\n<br/>\r\n
      8. No stalls, shops or booths are currently operational on platforms.`
    },
    {
      question: `What can you do to stay safe?`,
      answer: `1. Carry extra masks, gloves, wet/dry wipes, and a tiny bottle of sanitizer for quick use. Keep your mouth and nose covered at all times and regularly clean your hands.\r\n<br/>\r\n
      2. Carry a small pouch, which holds all these essentials and keep it handy in your handbag.\r\n<br/>\r\n
      3. Do not be in a hurry as this can cause panic. Reach the station well in advance, stay calm and help keep others (especially children and senior citizens around you) calm as well.\r\n<br/>\r\n
      4. Bring home cooked food and sufficient water for your journey.\r\n<br/>\r\n
      5. Those with compatible phones should download Arogya Setu on their apps so they can check the health of people around.`
    }
  ];
  responseData.infos = [
    {
      query: `IRCTC Special Trains Being Run During Covid-19 Lockdown:`,
      info: `Indian Railways has famously been called the symbol of this country’s gati (speed) and pragati (progress) and in these tough times, yet again, it has risen to face the challenges that the Covid-19 lockdown has thrown. While regular train services remain currently suspended, many special trains have been announced to ferry passengers to their destination safely.`
    },
    {
      query: `Special Trains in the Initial Phase of Lockdown:`,
      info: `Since 12 May, 15 new pairs of special trains (30 return journeys) have been running on daily, bi-weekly and tri-weekly basis, while following proper hygiene and strict social distancing protocols. Starting from New Delhi, these trains connect to cities Dibrugarh, Agartala, Howrah, Patna, Bilaspur, Ranchi, Bhubaneswar, Secunderabad, Bengaluru, Chennai, Thiruvananthapuram, Madgaon, Mumbai Central, Ahmedabad and Jammu Tawi.`
    },
    {
      query: `Shramik and Other Special Trains:`,
      info: `Shramik trains that provide train services to stranded migrants who want to travel to their native states and districts are also currently being operated. In addition, many other AC and non-AC special passenger trains (more than 200) have been announced since June 01. These are fully reserved trains with special amenities to ensure safety of all on board.`
    },
    {
      query: `Plan Your Trip:`,
      info: `Check MakeMyTrip’s app and website for full details on all these trains, including available dates, schedule as well as the exact route the trains take. Once you have planned your trip, you can proceed to the MakeMyTrip website or mobile app to make your booking.`
    },
    {
      query: `Safety First:`,
      info: `Indian Railways has announced multiple measures to keep their passengers safe. For instance only online bookings are being allowed to avoid queues at stations and those with symptoms are not being allowed to board the train, to name a few. `
    }
  ];
  responseData.template = {
    // created: `Tue May 14 2019 11:08:50 GMT+0530 (India Standard Time)`,
    // modified: `Thu Jan 02 2020 06:16:07 GMT+0530 (India Standard Time)`,
    // id: `1`,
    template_name: `route`,
    // type: `ab`,
    meta_title: `Covid 19 IRCTC Special Trains, Shramik Special Trains Ticket Booking - MakeMyTrip`,
    meta_description: `IRCTC Covid 19 Special Trains Schedule and Reservation of IRCTC Shramik special trains by Indian railways can be booked from MakeMyTrip. Around 260 Covid Special Trains already functional & Check out availability.`,
    meta_keywords: `IRCTC Covid Special Trains, IRCTC shramik special trains, Corona Special Train, Special Trains, Special Trains Schedule, Train Ticket Booking, IRCTC Ticket Booking, IRCTC Train Tickets, train enquiry, rails information, train timetable, train schedule, PNR status, Indian Railways Reservation, Covid special trains tatkal tickets, Tatkal tickets for covid special trains, Covid special trains tatkal reservation`,
    analytics: getStaticPageLoadEvent('seo:rails:special:trains'),
    footer_data: FOOTER_DATA_IRCTC,
    h1_title: `IRCTC Covid 19 Special Trains`,
    breadcrumb: [
      {
        redirectionUrl: `https://www.makemytrip.com/`,
        displayString: `Home`
      },
      {
        redirectionUrl: `https://www.makemytrip.com/railways/`,
        displayString: `Rails`
      },
      {
        displayString: `Covid Special Trains`,
        redirectionUrl: `https://www.makemytrip.com/railways/covid-special-trains.html`
      }
    ],
    banner_desktop: ``,
    banner_mobile: ``,
    sample_url: `https://www.makemytrip.com/railways/covid-special-trains.html`,
    // sub_title: `Following is the list of all the trains running between Delhi to Mumbai Railway Stations:`,
    // eslint-disable-next-line
    external_js: IRCTC_EXTERNAL_JS,
    extra_info: ``
  };
  let finalResponse;
  try {
    finalResponse = decorateListingData(responseData);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('err', e);
  }
  const pageType = 'trains';
  const cacheKey = 'HTML_STATIC_COVID-SPECIAL-TRAINS.HTML';
  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    pageType,
    ...finalResponse
  };
};

export default CovidSpecialTrainsPage;
